// منطقِ خالصِ ثبتِ رویداد: پاکسازیِ props و صف.
//
// از `analytics.ts` جدا شده چون آن یکی به supabase وصل است و بقیه‌ی core هم
// همین قاعده را دارد: چیزی که تست می‌شود، هیچ وابستگیِ عارضه‌داری ندارد
// (مثل roster.ts و contacts.ts و smsInvite.ts). این‌طوری با `npm test` مستقیم
// اجرا می‌شود، بدونِ دیتابیس و بدونِ مرورگر.

/** ردیفی که به جدولِ `app_events` می‌رود. */
export interface EventRow {
  user_id: string | null;
  session_id: string;
  surface: 'web' | 'mobile';
  name: string;
  props: Record<string, unknown>;
  app_version: string | null;
  /**
   * چهار ستونِ «سفرِ کاربر» — مایگریشن 0120. همه اختیاری‌اند تا ردیف‌های
   * پیش از آن مایگریشن همچنان معتبر باشند.
   *
   * ⚠ `occurred_at` و `seq` با هم ترتیب را نگه می‌دارند و این **لازم** است،
   * نه اضافه‌کاری: `created_at` را دیتابیس با `now()` پر می‌کند و `now()` در
   * یک تراکنش ثابت است، پس هر ده ردیفِ یک دسته دقیقاً یک زمان می‌گیرند و
   * ترتیبِ داخلِ دسته گم می‌شود. (همین در `logs/current.log` هم دیده می‌شود:
   * ده‌ها سطر با زمانِ یکسان.) `seq` شمارنده‌ی همان نشست است و حتی اگر
   * ساعتِ گوشی عقب/جلو باشد ترتیب را نگه می‌دارد.
   */
  occurred_at?: string;
  seq?: number;
  /** صفحه‌ای که این اتفاق **رویش** افتاد — برای لمس‌ها هم پر می‌شود. */
  screen?: string | null;
  /** کلیدِ ترجمه‌ی دکمه (`chip:inviteFriends`)، نه متنش. */
  label?: string | null;
  /** مدت، برای رویدادهایی که مدت دارند (`session_end`). */
  ms?: number | null;
}

/* ─────────────────────────── پاکسازی ─────────────────────────── */

/**
 * تنها شکل‌های مجازِ مقدار در `props`.
 *
 * رشته باید کوتاه و «برچسب‌مانند» باشد. این الگو عمداً `@` و فاصله و `+` را
 * رد می‌کند — یعنی ایمیل، شماره و متنِ آزاد هیچ‌کدام رد نمی‌شوند، حتی اگر
 * کسی از تایپ‌ها فرار کرده باشد.
 *
 * چرا لایه‌ی دوم لازم است وقتی تایپ‌ها هستند: `any` همیشه یک جایی پیدا
 * می‌شود، و چیزی که این‌جا قفل می‌شود یک قولِ حریمِ خصوصی است نه یک
 * جزئیاتِ فنی.
 */
const LABEL = /^[A-Za-z0-9_.:/-]{1,40}$/;

export function sanitizeProps(props: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props ?? {})) {
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
    else if (typeof v === 'boolean') out[k] = v;
    else if (typeof v === 'string' && LABEL.test(v)) out[k] = v;
    // هر چیزِ دیگر — شیء، آرایه، رشته‌ی بلند، null — بی‌صدا می‌افتد. بی‌صدا
    // چون این مسیر نباید هیچ‌وقت مزاحم شود؛ نبودنِ یک کلید در داشبورد خودش
    // به‌اندازه‌ی کافی گویا است.
  }
  return out;
}

/* ─────────────────────────── مُهرِ کاربر ─────────────────────────── */

/**
 * ردیف‌ها را با کسی که **همین حالا** وارد است دوباره مهر می‌زند.
 *
 * ⚠ این نه یک بهینه‌سازی است نه یک احتیاط — بدونش سیاستِ RLS کلِ دسته را رد
 * می‌کند. `app_events` می‌گوید `user_id is not distinct from auth.uid()`
 * (مایگریشن 0068)، و آن `auth.uid()` **لحظه‌ی insert** سنجیده می‌شود، نه
 * لحظه‌ی `track()`.
 *
 * سناریو، که در لاگِ ۲۰۲۶-۰۸-۱۸..۲۰ سیزده بار افتاد: اپ بالا می‌آید،
 * `screen_view` در همان چند صدم ثانیه‌ی اول ثبت می‌شود در حالی که نشست هنوز
 * خوانده نشده، پس `user_id` خالی مهر می‌خورد. چند ثانیه بعد که صف خالی
 * می‌شود، نشست رسیده و `auth.uid()` یک uuid است. «خالی» با آن uuid یکی
 * نیست، پس **کلِ دسته** رد می‌شود — نه فقط آن یک ردیف.
 *
 * چرا مهرِ تازه دروغ نیست: کاربر همان موقع هم وارد بود؛ ما فقط هنوز نشستش
 * را نخوانده بودیم. مهرِ خالی ادعایی درباره‌ی کاربر نبود، نبودِ جواب بود.
 *
 * و شکلِ برعکسش هم پوشیده می‌شود: ردیفی که با uuid مهر خورده ولی کاربر
 * قبل از خالی شدنِ صف خارج شده، حالا `null` می‌گیرد و می‌نشیند — تنها
 * مقداری که سیاست از یک درخواستِ ناشناس می‌پذیرد.
 */
export function stampSender<Row extends { user_id: string | null }>(
  rows: Row[], who: string | null,
): Row[] {
  // آرایه‌ی تازه فقط وقتی لازم است که واقعاً چیزی عوض شود؛ حالتِ عادی همین است.
  return rows.every((r) => r.user_id === who)
    ? rows : rows.map((r) => (r.user_id === who ? r : { ...r, user_id: who }));
}

/* ─────────────────────────── صف ─────────────────────────── */

/** بعد از این تعداد، بدونِ انتظار فرستاده می‌شود. */
export const BATCH = 10;
/** و اگر به آن تعداد نرسید، حداکثر این‌قدر منتظر می‌ماند. */
export const FLUSH_MS = 15_000;
/**
 * سقفِ صف. اگر شبکه قطع باشد و صف بالا برود، قدیمی‌ها می‌افتند نه تازه‌ها:
 * وضعیتِ **الان** بیشتر به درد می‌خورد تا ده دقیقه پیش.
 */
export const MAX_QUEUE = 200;

/**
 * صفِ دسته‌ای — روی نوعِ ردیف عمومی است، چون `lib/devlog` هم دقیقاً همین را
 * می‌خواهد با ردیفی از شکلِ دیگر. دو پیاده‌سازیِ موازیِ همین ده خط، دو جای
 * متفاوت برای همان اشتباه است.
 */
export interface Queue<Row> {
  /** اگر دسته پر شد خودِ دسته را می‌دهد و صف را خالی می‌کند، وگرنه `null`. */
  push: (row: Row) => Row[] | null;
  take: () => Row[];
  size: () => number;
}

export function makeQueue<Row = EventRow>(batch = BATCH, max = MAX_QUEUE): Queue<Row> {
  let rows: Row[] = [];
  return {
    push(row) {
      rows.push(row);
      if (rows.length > max) rows = rows.slice(-max);
      if (rows.length >= batch) {
        const out = rows;
        rows = [];
        return out;
      }
      return null;
    },
    take() {
      const out = rows;
      rows = [];
      return out;
    },
    size: () => rows.length,
  };
}

/* ─────────────────────────── تکراری ─────────────────────────── */

/**
 * آیا این `screen_view` تکرارِ بی‌معنیِ قبلی است.
 *
 * ═══ چرا لازم شد ═══
 * در یک بازدیدِ آزمایشی هر صفحه **دو بار** ثبت شد. مقصر `React.StrictMode`
 * بود که در حالتِ توسعه هر افکت را دو بار صدا می‌زند. روی بیلدِ محصول این
 * رفتار وجود ندارد، پس می‌شد نادیده‌اش گرفت — ولی آن‌وقت عددهای حالتِ توسعه
 * همیشه دو برابرِ واقعیت بودند و کسی که به داشبورد نگاه می‌کند نمی‌داند
 * کدامش واقعی است.
 *
 * رفتن از یک صفحه به **خودش** در روتر ممکن نیست (مسیر عوض نشده)، پس دو
 * `screen_view`ِ پشتِ سرِ هم با یک مقدار، همیشه یک artifact است.
 */
export const isRepeatScreen = (
  lastScreen: string | null, name: string, props: Record<string, unknown>,
): boolean => name === 'screen_view' && props.screen === lastScreen;

/* ─────────────────────────── نشست ─────────────────────────── */

/**
 * شناسه‌ی یک بازدید. ذخیره نمی‌شود: «نشست» یعنی یک بار باز کردنِ اپ، و
 * ماندگار کردنش این معنی را از بین می‌برد.
 */
export const newSessionId = (): string => {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  // بعضی محیط‌های ری‌اکت‌نیتیو و مرورگرهای قدیمی randomUUID ندارند. این
  // شناسه رمزنگارانه نیست و لازم هم نیست باشد — فقط باید یکتا باشد.
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};
